import {
  parseBundleName,
  BUNDLE_PREFIX,
} from '@wix/editor-elements-conventions';
import {
  ILibraryManifestContext,
  ILibraryAssetsPatterns,
  IWebpackManifestContext,
} from '../registry.types';
import { createInternalException } from '../errors/RegistryError';
import { isCSS, isJS, getExtension } from '../toolbox/assets';
import { createLibraryManifestBuilder } from './builders/libraryManifestBuilder';

const PREFIX_REGEXP = new RegExp(BUNDLE_PREFIX, 'g');

export function parseWebpackManifest(
  library: IWebpackManifestContext,
  libraryAssetsPatterns: ILibraryAssetsPatterns = [],
): ILibraryManifestContext {
  const libraryBuilder = createLibraryManifestBuilder({
    topology: library.topology,
  });

  const {
    'webpack-runtime.js': webpackRuntimeBundleURL,
    'hot.js': hotBundleURL,
    ...components
  } = library.manifest;

  if (webpackRuntimeBundleURL) {
    libraryBuilder.addWebpackRuntimeBundle(webpackRuntimeBundleURL);
  }

  if (hotBundleURL) {
    libraryBuilder.addHotBundle(hotBundleURL);
  }

  Object.keys(components).forEach(key => {
    /**
     * If webpack cache group is created automatically,
     * its name may includes multiple entries names
     * eb rb_wixui.thunderbolt[Button]~rb_wixui.thunderbolt[Icon]
     */
    const isWebpackCacheGroup = key.match(PREFIX_REGEXP);

    if (isWebpackCacheGroup && isWebpackCacheGroup.length > 1) {
      return;
    }

    const symbols = parseBundleName(key);

    if (symbols === null) {
      return;
    }

    const { host, componentName, part, id, batch, extension } = symbols;

    /**
     * If bundle format does not match convention rules.
     * `host` should always be always decoded from the bundle name
     */
    if (!host || batch) {
      return;
    }

    const hostBuilder = libraryBuilder.getHostBuilder(host);

    const url = components[key];

    if (isJS(extension)) {
      if (!componentName && !part) {
        hostBuilder.addUrl(id, url);
      } else if (componentName) {
        const componentBuilder = hostBuilder.getComponentBuilder(componentName);

        if (part) {
          componentBuilder.addPart(id, part, url);
        } else {
          componentBuilder.addUrl(id, url);
        }
      } else {
        throw createInternalException();
      }
    } else if (isCSS(extension)) {
      if (!componentName && !part) {
        hostBuilder.addStyleAsset(url);
      } else if (componentName) {
        const componentBuilder = hostBuilder.getComponentBuilder(componentName);
        componentBuilder.addStyleAsset(url, part);
      } else {
        throw createInternalException();
      }
    }
  });

  libraryAssetsPatterns!.forEach(({ type, pattern }) => {
    Object.keys(library.manifest).forEach(id => {
      const url = library.manifest[id];

      if (pattern.test(id)) {
        const extension = getExtension(url);
        libraryBuilder.addLibraryAsset(url, type, extension);
      }
    });
  });

  return libraryBuilder.build();
}
